import { useCallback, useEffect, useRef, useState } from 'react';

export interface UseScaleReaderResult {
  /** True while the local scale-bridge WebSocket is open and reporting the serial port connected. */
  connected: boolean;
  /** Last weight reading received, in kg. Updated continuously regardless of `live`. */
  weight_kg: number | null;
  /** String error code (never a thrown exception) — e.g. `'scale_bridge_unreachable'`. */
  error: string | null;
  /** True while the caller wants readings mirrored into its own field (key held + focused). */
  live: boolean;
  startLive: () => void;
  stopLive: () => void;
}

interface ScaleBridgeMessage {
  type?: string;
  kg?: number;
  connected?: boolean;
}

const SCALE_BRIDGE_WS_URL = 'ws://localhost:17654';
const RECONNECT_DELAY_MS = 3000;

/**
 * Connects to the local `@novapet/scale-bridge` process (one per workstation
 * with a Rhino I-QY attached) over `ws://localhost` — not the Web Serial API,
 * since this app has no secure-context guarantee across POS workstations.
 * Opens the connection as soon as it's used (so the connection status badge
 * is meaningful even before the user holds the read key) and keeps
 * reconnecting for as long as the hook stays mounted; never throws.
 */
export function useScaleReader(): UseScaleReaderResult {
  const [connected, setConnected] = useState(false);
  const [weight_kg, setWeightKg] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [live, setLive] = useState(false);

  const socket_ref = useRef<WebSocket | null>(null);
  const reconnect_timer_ref = useRef<number | null>(null);
  const mounted_ref = useRef(true);

  const connect = useCallback((): void => {
    if (socket_ref.current || !mounted_ref.current) return;
    try {
      const socket = new WebSocket(SCALE_BRIDGE_WS_URL);
      socket_ref.current = socket;

      socket.onopen = () => {
        setError(null);
      };
      socket.onmessage = (event) => {
        try {
          const message = JSON.parse(String(event.data)) as ScaleBridgeMessage;
          if (message.type === 'weight' && typeof message.kg === 'number') {
            setWeightKg(message.kg);
          } else if (message.type === 'status' && typeof message.connected === 'boolean') {
            setConnected(message.connected);
          }
        } catch {
          // malformed frame — ignore, next one will self-correct
        }
      };
      socket.onerror = () => {
        setError('scale_bridge_unreachable');
      };
      socket.onclose = () => {
        socket_ref.current = null;
        setConnected(false);
        if (mounted_ref.current) {
          reconnect_timer_ref.current = window.setTimeout(connect, RECONNECT_DELAY_MS);
        }
      };
    } catch {
      setError('scale_bridge_unreachable');
      reconnect_timer_ref.current = window.setTimeout(connect, RECONNECT_DELAY_MS);
    }
  }, []);

  useEffect(() => {
    mounted_ref.current = true;
    connect();
    return () => {
      mounted_ref.current = false;
      if (reconnect_timer_ref.current !== null) {
        window.clearTimeout(reconnect_timer_ref.current);
        reconnect_timer_ref.current = null;
      }
      socket_ref.current?.close();
      socket_ref.current = null;
    };
  }, [connect]);

  const startLive = useCallback((): void => setLive(true), []);
  const stopLive = useCallback((): void => setLive(false), []);

  return { connected, weight_kg, error, live, startLive, stopLive };
}
